/**
 * Settings & initialization actions — group, period, viewing learner, dev/demo.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { SetFn, GetFn, AppStoreState } from './types'
import type { GroupId, StartingPeriod } from '../../config/systemConfig'
import { GROUPS } from '../../config/systemConfig'
import { DEFAULT_GROUP } from '../../config/constants'
import {
  ensureAdminAccount,
  loadState,
  saveState,
  createFreshState,
  generateDemoAccounts,
  seedDemoAssessments,
} from '../../accounts'
import { dataLayer } from '../../data/dataLayer'
import { debug, error as logError, warn } from '../../utils/logger'

type SettingsActions = Pick<
  AppStoreState,
  | 'initialize'
  | 'setSelectedGroup'
  | 'setStartingPeriod'
  | 'setViewingLearner'
  | 'devResetProgress'
  | 'loadDemoAccounts'
>

export function createSettingsActions(set: SetFn, get: GetFn): SettingsActions {
  return {
    initialize: async () => {
      const state = get()
      if (state.isInitialized) return

      // Load persisted group/period from dataLayer
      try {
        const storedGroup = await dataLayer.getGroup()
        if (storedGroup) {
          const parsed = storedGroup as GroupId
          if (GROUPS.includes(parsed)) {
            set({ selectedGroup: parsed })
          }
        }
        const storedPeriod = await dataLayer.getPeriod()
        if (storedPeriod) {
          set({ manualPeriod: storedPeriod as StartingPeriod })
        }
      } catch {
        // Ignore parse errors
      }

      // Load multi-account state (V4 only)
      const loadedState = await loadState()
      const migratedState = {
        ...loadedState,
        accounts: ensureAdminAccount(loadedState.accounts),
      }
      saveState(migratedState)
      set({ multiAccountState: migratedState, isInitialized: true })
    },

    setSelectedGroup: (group) => {
      if (!GROUPS.includes(group)) {
        warn(`Invalid group "${group}" — ignoring setSelectedGroup call`)
        return
      }
      set((s) => ({
        selectedGroup: group,
        groupVersion: s.groupVersion + 1,
      }))
      dataLayer.setGroup(group)
      debug(`Group switched to ${group}`)
    },

    setStartingPeriod: (period) => {
      set((s) => ({
        manualPeriod: period,
        groupVersion: s.groupVersion + 1,
      }))
      if (period) {
        dataLayer.setPeriod(period)
      } else {
        dataLayer.clearPeriod()
      }
      debug(`Starting period set to ${period || 'AUTO'} - context updated`)
    },

    setViewingLearner: (learnerId) => {
      set({ viewingLearnerId: learnerId })
      debug(`Viewing learner set to: ${learnerId || 'none'}`)
    },

    devResetProgress: () => {
      const freshState = createFreshState()
      set((s) => ({
        multiAccountState: freshState,
        groupVersion: s.groupVersion + 1,
      }))
      saveState(freshState)
    },

    loadDemoAccounts: async () => {
      const demoState = generateDemoAccounts()
      // Djayden (grade 7) is the active account — sync selectedGroup
      const activeId = demoState.accounts.activeAccountId
      const activeAccount = demoState.accounts.accountsById[activeId]
      const activeGrade =
        activeAccount?.role === 'learner' && 'grade' in activeAccount
          ? (activeAccount as { grade?: number }).grade
          : undefined
      const groupToSet =
        activeGrade && GROUPS.includes(activeGrade as GroupId)
          ? (activeGrade as GroupId)
          : DEFAULT_GROUP
      set((s) => ({
        multiAccountState: demoState,
        selectedGroup: groupToSet,
        groupVersion: s.groupVersion + 1,
      }))
      dataLayer.setGroup(groupToSet)
      saveState(demoState)

      // Seed assessment data (awaited so data is available when pages render)
      const accounts = Object.values(demoState.accounts.accountsById)
      const djayden = accounts.find((a) => a.displayName === 'Djayden')
      const sophie = accounts.find((a) => a.displayName === 'Sophie')
      const emma = accounts.find((a) => a.displayName === 'Emma')
      const noah = accounts.find((a) => a.displayName === 'Noah')
      const liam = accounts.find((a) => a.displayName === 'Liam')
      const noor = accounts.find((a) => a.displayName === 'Noor')
      const meesterJay = accounts.find((a) => a.displayName === 'Meester Jay')
      const classroomIds = Object.keys(demoState.accounts.classrooms || {})
      const firstClassroomId = classroomIds[0]
      if (djayden && sophie) {
        try {
          await seedDemoAssessments(
            {
              djayden: djayden.id,
              sophie: sophie.id,
              emma: emma?.id || '',
              noah: noah?.id || '',
              liam: liam?.id || '',
              noor: noor?.id || '',
            },
            meesterJay?.id,
            firstClassroomId,
          )
        } catch (err) {
          logError('Failed to seed assessments', err)
        }
      }

      debug('✅ Demo accounts loaded successfully')
    },
  }
}
